import React from "react";
import { useUpdateEffect } from "react-use";
import { isFunction } from "lodash-es";
const deserializer = JSON.parse;
const serializer = JSON.stringify;
const processOnGet = (value) => value;
const processOnSet = (value) => value;
// here updating the value won't trigger re-rendering; needed to store expanded state of a tree
export const useLocalStorageAsRef = (key, defaultValue, options) => {
    const state = React.useRef(getFromLocalStorage(key, defaultValue, options));
    const defaultValueRef = React.useRef(defaultValue);
    defaultValueRef.current = defaultValue;
    const optionsRef = React.useRef(options);
    optionsRef.current = options;
    useUpdateEffect(() => {
        state.current = getFromLocalStorage(key, defaultValueRef.current, optionsRef.current);
    }, [key]);
    const set = React.useCallback(newValueOrFn => {
        const newValue = isFunction(newValueOrFn)
            ? newValueOrFn(state.current)
            : newValueOrFn;
        setToLocalStorage(key, newValue, optionsRef.current);
        state.current =
            newValue !== undefined ? newValue : defaultValueRef.current;
    }, [key]);
    return [state, set];
};
// here we're updating the value and trigger re-rendering
export const useLocalStorageAsState = (key, defaultValue, subscribeToStorageEvents = false, options) => {
    const [state, setState] = React.useState(getFromLocalStorage(key, defaultValue, options));
    const stateRef = React.useRef(state);
    stateRef.current = state;
    const defaultValueRef = React.useRef(defaultValue);
    defaultValueRef.current = defaultValue;
    const optionsRef = React.useRef(options);
    optionsRef.current = options;
    useUpdateEffect(() => {
        setState(getFromLocalStorage(key, defaultValueRef.current, optionsRef.current));
    }, [key]);
    const set = React.useCallback(newValueOrFn => {
        const newValue = isFunction(newValueOrFn)
            ? newValueOrFn(stateRef.current)
            : newValueOrFn;
        setToLocalStorage(key, newValue, optionsRef.current);
        setState(newValue !== undefined ? newValue : defaultValueRef.current);
    }, [key]);
    // subscribe to the storage event
    React.useEffect(() => {
        if (!subscribeToStorageEvents)
            return;
        const handler = (e) => {
            if (e.storageArea === localStorage && e.key === key) {
                try {
                    if (e.newValue) {
                        setState(deserializer(e.newValue));
                    }
                    else {
                        setState(defaultValueRef.current);
                    }
                }
                catch (e) {
                    setState(defaultValueRef.current);
                    if (e instanceof Error)
                        console.error(e);
                }
            }
        };
        window.addEventListener("storage", handler);
        return () => {
            window.removeEventListener("storage", handler);
        };
    }, [subscribeToStorageEvents, key]);
    return [state, set];
};
export const getFromLocalStorage = (key, defaultValue, options) => {
    const { storeDefaultValue = false } = options ?? {};
    try {
        const localStorageValue = localStorage.getItem(key);
        if (localStorageValue !== null) {
            const value = (options?.processOnGet ?? processOnGet)((options?.deserializer ?? deserializer)(localStorageValue));
            const newValue = (options?.processOnSet ?? processOnSet)(value);
            localStorage.setItem(key, (options?.serializer ?? serializer)(newValue));
            return value;
        }
        if (storeDefaultValue) {
            const newValue = (options?.processOnSet ?? processOnSet)(defaultValue);
            localStorage.setItem(key, (options?.serializer ?? serializer)(newValue));
        }
    }
    catch (e) {
        // do nothing
        if (e instanceof Error)
            console.error(e);
    }
    return defaultValue;
};
export const setToLocalStorage = (key, value, options) => {
    try {
        if (value === undefined) {
            localStorage.removeItem(key);
        }
        else {
            const newValue = (options?.processOnSet ?? processOnSet)(value);
            localStorage.setItem(key, (options?.serializer ?? serializer)(newValue));
            options?.onSet?.(newValue);
        }
    }
    catch (e) {
        // do nothing
        if (e instanceof Error)
            console.error(e);
    }
};
//# sourceMappingURL=useLocalStorage.js.map